import React, { useState, useEffect } from 'react';
import { Table, Button, Drawer, Form, Input, Space, Popconfirm, message } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import rbacService from '../../services/rbacService';
import useBreakpoint from '../../hooks/useBreakpoint';

const { TextArea } = Input;

/**
 * Permissions Management Component
 * Allows viewing, editing, and deleting permissions
 */
export default function PermissionsManagement() {
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedPermission, setSelectedPermission] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [tableSearchText, setTableSearchText] = useState('');
  const [form] = Form.useForm();
  const isAboveSm = useBreakpoint('sm');

  const isEditMode = !!selectedPermission;

  const filteredPermissions = tableSearchText.trim()
    ? permissions.filter(
        (p) =>
          (p.key || '').toLowerCase().includes(tableSearchText.toLowerCase()) ||
          (p.description || '').toLowerCase().includes(tableSearchText.toLowerCase())
      )
    : permissions;

  useEffect(() => {
    fetchPermissions();
  }, []);

  const fetchPermissions = async () => {
    setLoading(true);
    try {
      const result = await rbacService.getPermissions();
      if (result.success) {
        // Ensure data is always an array
        const data = result.data;
        setPermissions(Array.isArray(data) ? data : []);
      } else {
        message.error(result.message || 'Failed to fetch permissions');
        setPermissions([]);
      }
    } catch (error) {
      message.error('Error loading permissions');
      console.error('Error fetching permissions:', error);
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  };

  const openCreateDrawer = () => {
    setSelectedPermission(null);
    form.resetFields();
    setDrawerVisible(true);
  };

  const handleEdit = (record) => {
    setSelectedPermission(record);
    form.setFieldsValue({
      key: record.key,
      description: record.description || '',
    });
    setDrawerVisible(true);
  };

  const handleDelete = async (permissionId) => {
    try {
      const result = await rbacService.deletePermission(permissionId);
      if (result.success) {
        message.success('Permission deleted successfully');
        fetchPermissions();
      } else {
        message.error(result.message || 'Failed to delete permission');
      }
    } catch (error) {
      message.error('Error deleting permission');
      console.error('Error deleting permission:', error);
    }
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      if (isEditMode) {
        const result = await rbacService.updatePermission(selectedPermission.id, values);
        if (result.success) {
          message.success('Permission updated successfully');
          setDrawerVisible(false);
          setSelectedPermission(null);
          form.resetFields();
          fetchPermissions();
        } else {
          message.error(result.message || 'Failed to update permission');
        }
      } else {
        const result = await rbacService.createPermission(values);
        if (result.success) {
          message.success('Permission created successfully');
          setDrawerVisible(false);
          setSelectedPermission(null);
          form.resetFields();
          fetchPermissions();
        } else {
          message.error(result.message || 'Failed to create permission');
        }
      }
    } catch (error) {
      const errMsg = error?.response?.data?.message || error?.message;
      message.error(errMsg || (isEditMode ? 'Error updating permission' : 'Error creating permission'));
      console.error(isEditMode ? 'Error updating permission:' : 'Error creating permission:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      title: 'Permission Key',
      dataIndex: 'key',
      key: 'key',
      sorter: (a, b) => (a.key || '').localeCompare(b.key || ''),
      render: (text) => (
        <span className="font-mono text-sm font-medium">{text}</span>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (text) => text || <span className="text-gray-400">No description</span>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: isAboveSm ? 130 : 110,
      fixed: isAboveSm ? 'right' : undefined,
      render: (_, record) => (
        <Space size={isAboveSm ? 'small' : 'middle'} wrap>
          <Button
            icon={<EditOutlined />}
            size={isAboveSm ? 'small' : 'middle'}
            onClick={() => handleEdit(record)}
            title="Edit Permission"
            className={!isAboveSm ? '!px-2' : ''}
          />
          <Popconfirm
            title="Delete Permission"
            description="Are you sure you want to delete this permission? This action cannot be undone."
            onConfirm={() => handleDelete(record.id)}
            okText="Yes, Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              size={isAboveSm ? 'small' : 'middle'}
              title="Delete Permission"
              className={!isAboveSm ? '!px-2' : ''}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Action Bar */}
      <div className="mb-4 flex flex-wrap justify-between items-center gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Permissions</h2>
          <p className="text-sm text-gray-600 mt-1">Manage system permissions</p>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={openCreateDrawer}
          size="large"
        >
          Create Permission
        </Button>
      </div>

      {/* Table Search */}
      <div className="mb-3">
        <Input.Search
          placeholder="Search by key or description..."
          allowClear
          enterButton={<><SearchOutlined /> Search</>}
          value={tableSearchText}
          onChange={(e) => setTableSearchText(e.target.value)}
          onSearch={setTableSearchText}
          className="max-w-md"
        />
      </div>

      {/* Table */}
      <Table
        columns={columns}
        dataSource={filteredPermissions}
        loading={loading}
        rowKey="id"
        scroll={{ x: 800 }}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} permissions`,
          showQuickJumper: true,
        }}
        className="rbac-table"
      />

      {/* Add custom CSS for table */}
      <style>{`
        .rbac-table .ant-table {
          font-size: 14px;
        }
        .rbac-table .ant-table-thead > tr > th {
          background: #F9FAFB;
          font-weight: 600;
          color: #374151;
          border-bottom: 2px solid #E5E7EB;
        }
        .rbac-table .ant-table-tbody > tr:hover > td {
          background: #F3F4F6;
        }
        .rbac-table .ant-pagination {
          margin-top: 24px;
        }
      `}</style>

      {/* Create / Edit Permission Drawer */}
      <Drawer
        title={isEditMode ? 'Edit Permission' : 'Create Permission'}
        open={drawerVisible}
        onClose={() => {
          setDrawerVisible(false);
          setSelectedPermission(null);
          form.resetFields();
        }}
        width={500}
        destroyOnClose
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            label="Permission Key"
            name="key"
            rules={[
              { required: true, message: 'Please enter permission key' },
              {
                pattern: /^[a-zA-Z0-9_]+$/,
                message: 'Key must be letters (upper/lowercase), numbers, and underscores only (e.g., New_Feature_Access)',
              },
            ]}
          >
            <Input
              placeholder="e.g., New_Feature_Access"
              disabled={isEditMode}
            />
          </Form.Item>

          <Form.Item
            label="Description"
            name="description"
            rules={[
              { max: 500, message: 'Description cannot exceed 500 characters' },
            ]}
          >
            <TextArea
              rows={4}
              placeholder="e.g., Access to new feature"
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
              >
                {isEditMode ? 'Update Permission' : 'Create Permission'}
              </Button>
              <Button
                onClick={() => {
                  setDrawerVisible(false);
                  setSelectedPermission(null);
                  form.resetFields();
                }}
                disabled={submitting}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
}
